/**
 * Admin sidebar (280px expanded, 64px collapsed icon-rail).
 *
 * PRD §9.2: nav links — Agents, Users, Sheets, Conversations, Settings, …
 * Every row renders through the shared `SidebarNavItem` primitive so its
 * active-state, spacing, and hover behaviour match the user-portal sidebar
 * exactly.
 *
 * UST-3088: the expanded rail groups those links under four collapsible
 * categories (AI · Modules · Workspace · Administration); Modules nests one
 * level deeper for Marketing and Sales. Collapsed mode stays a flat icon rail
 * — 64px has no room for headings — and renders the same leaves in tree
 * order, so toggling collapse never reshuffles the icons.
 */
import type { SupportOdooHelpdeskStatus } from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  BookUser,
  Bot,
  Boxes,
  Briefcase,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Contact,
  Cpu,
  Headset,
  FolderOpen,
  Gauge,
  Globe,
  History,
  Inbox,
  Layers,
  LayoutTemplate,
  LifeBuoy,
  MessagesSquare,
  Scissors,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Table2,
  PhoneIncoming,
  ScrollText,
  Ticket,
  TrendingUp,
  Users,
  Wand2,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query-keys';
import { SidebarNavItem } from '../ds';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
}

/** A collapsible heading. `items` holds leaves and/or one more level of groups. */
interface NavGroup {
  /** Stable id — persisted in localStorage, so never rename an existing one. */
  key: string;
  label: string;
  /** Top-level categories carry an icon; nested ones read as plain headings. */
  icon?: LucideIcon;
  items: NavNode[];
}

type NavNode = NavItem | NavGroup;

function isGroup(node: NavNode): node is NavGroup {
  return 'items' in node;
}

const AGENTS_NAV_ITEM: NavItem = { to: '/admin/agents', icon: Bot, label: 'Agents' };

const NAV: NavGroup[] = [
  {
    key: 'ai',
    label: 'AI',
    icon: Cpu,
    // Automation + Odoo Control are appended at render time when enabled.
    items: [AGENTS_NAV_ITEM],
  },
  {
    key: 'modules',
    label: 'Modules',
    icon: Layers,
    items: [
      {
        key: 'modules.marketing',
        label: 'Marketing',
        items: [
          { to: '/admin/content-calendar', icon: Sparkles, label: 'Content Calendar' },
          { to: '/admin/media-calendar', icon: CalendarDays, label: 'Media Calendar' },
          { to: '/admin/visuals-generator', icon: Wand2, label: 'Visuals Generator' },
          { to: '/admin/video-editor', icon: Scissors, label: 'Video Editor' },
          { to: '/admin/template-fill', icon: LayoutTemplate, label: 'Template Fill' },
          { to: '/admin/seo', icon: TrendingUp, label: 'SEO' },
        ],
      },
      {
        key: 'modules.sales',
        label: 'Sales',
        items: [
          { to: '/admin/crm', icon: Contact, label: 'CRM' },
          { to: '/admin/leads', icon: Inbox, label: 'Contact us' },
        ],
      },
      // UST-3127 gave Support a second destination, so it is now a group.
      // The tickets page is a sibling path, not /admin/support/tickets —
      // nested, the inbox link would ALSO highlight on the tickets page.
      {
        key: 'modules.support',
        label: 'Support',
        items: [
          { to: '/admin/support', icon: LifeBuoy, label: 'Support Inbox' },
          { to: '/admin/support-tickets', icon: Ticket, label: 'Support Tickets' },
          { to: '/admin/support-monitor', icon: Gauge, label: 'Support Monitor' },
          { to: '/admin/telephony-support', icon: PhoneIncoming, label: 'Telephony Support' },
          { to: '/admin/support-contacts', icon: BookUser, label: 'Support Contacts' },
          { to: '/admin/support-contracts', icon: ScrollText, label: 'Support Contracts' },
          { to: '/admin/support-archive', icon: Archive, label: 'Support Archive' },
        ],
      },
      // Human Resources has no sub-pages yet, so it stays a clickable row
      // rather than a heading that opens onto nothing.
      { to: '/admin/hr', icon: Building2, label: 'Human Resources' },
      // Mail lives in the top-bar workspace switcher (it's universal, not a
      // gated module) — see WorkspaceSwitcher.
      // Communication tab is intentionally hidden — feature is still being
      // built out (channels are storage-only, no dispatchers wired yet). Keep
      // the route + page so we can flip it back on once connectors land.
      // { to: '/admin/communication', icon: MessageSquare, label: 'Communication' },
    ],
  },
  {
    key: 'workspace',
    label: 'Workspace',
    icon: Briefcase,
    items: [
      { to: '/admin/sheets', icon: Table2, label: 'Sheets' },
      { to: '/admin/files', icon: FolderOpen, label: 'Files' },
      { to: '/admin/scraper', icon: Search, label: 'Scraper' },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    icon: ShieldCheck,
    items: [
      { to: '/admin/users', icon: Users, label: 'Users' },
      { to: '/admin/audit', icon: History, label: 'Audit log' },
      { to: '/admin/conversations', icon: MessagesSquare, label: 'Conversations' },
      { to: '/admin/landing', icon: Globe, label: 'Website' },
      { to: '/admin/settings', icon: Settings, label: 'Settings' },
    ],
  },
];

// Appended to AI only while Odoo is enabled — for an admin caller,
// /odoo/access returning true already means "module enabled" (admins bypass
// the per-user grant check), so the one endpoint drives both this item and the
// top-nav Odoo tab.
const ODOO_NAV_ITEM: NavItem = { to: '/admin/odoo', icon: Boxes, label: 'Odoo Control' };

// UST-3143 — joins the Support group only while Odoo Helpdesk sync is on.
const ODOO_HELPDESK_NAV_ITEM: NavItem = {
  to: '/admin/odoo-helpdesk',
  icon: Headset,
  label: 'Odoo Helpdesk',
};

function withSupportItem(nodes: NavNode[], item: NavItem): NavNode[] {
  return nodes.map((node) =>
    !isGroup(node)
      ? node
      : node.key === 'modules.support'
        ? { ...node, items: [...node.items, item] }
        : { ...node, items: withSupportItem(node.items, item) },
  );
}

// Appended to AI only while the Automation module is enabled (for an admin,
// /automation/access true means "module enabled" since admins bypass the
// per-automation grant check).
const AUTOMATION_NAV_ITEM: NavItem = {
  to: '/admin/automation',
  icon: Workflow,
  label: 'Automation',
};

const COLLAPSE_KEY = 'pp.sidebar.collapsed';
// Stores the OPEN keys, so a category absent from the list — a new one, or a
// first visit — starts collapsed. Distinct from the old closed-keys entry so a
// stored list from before UST-3088 is never read with inverted meaning.
const OPEN_GROUPS_KEY = 'pp.sidebar.open-groups';

function readOpenGroups(): string[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

/** Depth-first leaves, used for the collapsed icon rail. */
function flattenNav(nodes: readonly NavNode[]): NavItem[] {
  return nodes.flatMap((node) => (isGroup(node) ? flattenNav(node.items) : [node]));
}

function NavGroupBlock({
  group,
  depth,
  openGroups,
  onToggle,
}: {
  group: NavGroup;
  depth: number;
  openGroups: readonly string[];
  onToggle: (key: string) => void;
}): JSX.Element {
  const open = openGroups.includes(group.key);
  const Icon = group.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(group.key)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center px-3 pt-3 pb-1 text-left font-semibold ' +
            'duration-instant ease-standard transition-colors',
          // A heading must outweigh the rows it owns: categories sit above the
          // 14px links, sub-categories between the two.
          depth === 0
            ? cn('gap-2 text-base', open ? 'text-accent' : 'text-ink hover:text-accent')
            : 'text-ink-soft hover:text-ink gap-1.5 pt-2 pl-5 text-[15px]',
        )}
      >
        {open ? (
          <ChevronDown className={depth === 0 ? 'size-4' : 'size-3.5'} />
        ) : (
          <ChevronRight className={depth === 0 ? 'size-4' : 'size-3.5'} />
        )}
        {Icon ? <Icon className="size-4" /> : null}
        <span>{group.label}</span>
      </button>

      {open ? (
        <ul className={cn('space-y-0.5', depth > 0 && 'pl-3')}>
          {group.items.map((node) =>
            isGroup(node) ? (
              <li key={node.key}>
                <NavGroupBlock
                  group={node}
                  depth={depth + 1}
                  openGroups={openGroups}
                  onToggle={onToggle}
                />
              </li>
            ) : (
              <li key={node.to}>
                <SidebarNavItem to={node.to} label={node.label} icon={node.icon} />
              </li>
            ),
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function Sidebar(): JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [openGroups, setOpenGroups] = useState<string[]>(readOpenGroups);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const toggleGroup = (key: string): void => {
    setOpenGroups((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  };

  const odooQuery = useQuery({
    queryKey: queryKeys.odoo.moduleAccess(),
    queryFn: () => apiRequest<{ access: boolean }>('/odoo/access'),
    staleTime: 60_000,
  });
  const automationQuery = useQuery({
    queryKey: queryKeys.automation.moduleAccess(),
    queryFn: () => apiRequest<{ access: boolean }>('/automation/access'),
    staleTime: 60_000,
  });
  // Automation + Odoo Control join the AI category, in that order, when enabled.
  const aiItems: NavNode[] = [AGENTS_NAV_ITEM];
  if (automationQuery.data?.access) aiItems.push(AUTOMATION_NAV_ITEM);
  if (odooQuery.data?.access) aiItems.push(ODOO_NAV_ITEM);
  const helpdeskQuery = useQuery({
    queryKey: queryKeys.support.odooHelpdesk(),
    queryFn: () => apiRequest<SupportOdooHelpdeskStatus>('/support/odoo-helpdesk'),
    staleTime: 60_000,
  });
  const baseNav: NavGroup[] = NAV.map((group) =>
    group.key === 'ai' ? { ...group, items: aiItems } : group,
  );
  const nav = helpdeskQuery.data?.enabled
    ? (withSupportItem(baseNav, ODOO_HELPDESK_NAV_ITEM) as NavGroup[])
    : baseNav;

  return (
    <aside
      className={cn(
        'border-rule bg-paper-soft ease-standard flex shrink-0 flex-col border-r transition-[width] duration-200',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
      aria-label="Primary navigation"
    >
      <nav className="flex-1 overflow-y-auto py-3">
        {collapsed ? (
          <ul className="space-y-0.5 px-0">
            {flattenNav(nav).map((item) => (
              <li key={item.to}>
                <SidebarNavItem to={item.to} label={item.label} icon={item.icon} collapsed />
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-2">
            {nav.map((group) => (
              <NavGroupBlock
                key={group.key}
                group={group}
                depth={0}
                openGroups={openGroups}
                onToggle={toggleGroup}
              />
            ))}
          </div>
        )}
      </nav>

      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={cn(
          'border-rule text-ink-muted inline-flex h-9 items-center gap-2 border-t px-3 text-xs ' +
            'duration-instant ease-standard hover:bg-paper-sunk hover:text-ink transition-colors',
          collapsed && 'justify-center px-0',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}
